/**
 * ValidationCurve — a grpo run's validation reward at each validation
 * step (TRA-1377): the mean over every sample as a solid ink line and each
 * validation dataset as a thin olive line told apart by its dash, then a
 * table of each dataset's mean at its first and latest validation. A
 * dataset row opens into its tasks, each from its first to its latest
 * validation, when the run's node counted them.
 */

import { Fragment, useState } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { TrainingValidationPoint } from '@/api/generated/model'
import {
  rewardDomain,
  stepTicks,
  type ValidationRow,
  validationDatasets,
  validationRows,
  validationSummary,
  validationTasks,
} from '@/components/training/progress'

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' }
/**
 * Each dataset in name order takes a dash and a tone of the brand palette
 * (brand-guidelines: Gunmetal Blue, Neutral Olive) beside the overall line's
 * black; tone and dash tell series apart, never hue (design-principles §3).
 * Both tones keep chart lines above 3:1 contrast on white (a lighter olive
 * would not); the dashes tell series apart past two.
 */
const DASHES = ['4 3', '1 3', '8 3 1 3', '2 2', '10 4']
const TONES = ['hsl(var(--gunmetal-blue))', 'hsl(var(--secondary))']

function dash(index: number): string {
  return DASHES[index % DASHES.length] ?? ''
}

function tint(index: number): string {
  return TONES[index % TONES.length] ?? 'hsl(var(--secondary))'
}

function DashMark({ index }: { index: number }): React.JSX.Element {
  return (
    <svg width="16" height="2" aria-hidden>
      <line x1="0" y1="1" x2="16" y2="1" stroke={tint(index)} strokeWidth={2} strokeDasharray={dash(index)} />
    </svg>
  )
}

function ValidationTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: readonly { payload?: unknown }[]
}): React.JSX.Element | null {
  const row = payload?.[0]?.payload as ValidationRow | undefined
  if (!active || !row) return null
  return (
    <div className="border border-border bg-card p-2.5 text-xs">
      <p className="font-mono uppercase tracking-wider text-muted-foreground">
        Step {row.step.toLocaleString()}
      </p>
      <p className="mt-1">
        All datasets: <span className="font-mono tabular-nums">{row.reward.toFixed(3)}</span>
      </p>
      {Object.entries(row.datasets).map(([name, reward]) => (
        <p key={name} className="mt-1">
          <span className="font-mono">{name}</span>:{' '}
          <span className="font-mono tabular-nums">{reward.toFixed(3)}</span>
        </p>
      ))}
    </div>
  )
}

function TaskRows({ points, name }: { points: TrainingValidationPoint[]; name: string }): React.JSX.Element {
  return (
    <>
      {validationTasks(points, name).map((task) => (
        <tr key={task.task} className="border-b border-border/60 text-muted-foreground">
          <td className="py-1.5 pl-8 font-mono text-xs">{task.task}</td>
          <td className="py-1.5 text-right font-mono text-xs tabular-nums">{task.samples.toLocaleString()}</td>
          <td className="py-1.5 text-right font-mono text-xs tabular-nums">{task.first.toFixed(3)}</td>
          <td className="py-1.5 text-right font-mono text-xs tabular-nums">{task.latest.toFixed(3)}</td>
        </tr>
      ))}
    </>
  )
}

function Summary({ points }: { points: TrainingValidationPoint[] }): React.JSX.Element {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set())
  const toggle = (name: string): void => {
    setOpen((current) => {
      const next = new Set(current)
      if (!next.delete(name)) next.add(name)
      return next
    })
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-border font-mono text-xs uppercase tracking-wider text-muted-foreground">
          <th className="py-2 text-left font-normal">Dataset</th>
          <th className="py-2 text-right font-normal">Samples</th>
          <th className="py-2 text-right font-normal">First</th>
          <th className="py-2 text-right font-normal">Latest</th>
        </tr>
      </thead>
      <tbody>
        {validationSummary(points).map((row, position) => {
          const tasks = position === 0 ? 0 : validationTasks(points, row.name).length
          const expanded = open.has(row.name)
          return (
            <Fragment key={row.name}>
              <tr className="border-b border-border">
                <td className="py-2 font-mono">
                  <span className="flex items-center gap-2">
                    {/* The first row is all datasets; the rest follow validationDatasets' order. */}
                    {position === 0 ? (
                      <span className="h-[2px] w-4 bg-foreground" aria-hidden />
                    ) : (
                      <DashMark index={position - 1} />
                    )}
                    {row.name}
                    {tasks > 0 && (
                      <button
                        type="button"
                        onClick={() => {
                          toggle(row.name)
                        }}
                        aria-expanded={expanded}
                        aria-label={`${expanded ? 'Hide' : 'Show'} the ${tasks.toLocaleString()} tasks of ${row.name}`}
                        className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                      >
                        {expanded ? 'Hide tasks' : `${tasks.toLocaleString()} tasks`}
                      </button>
                    )}
                  </span>
                </td>
                <td className="py-2 text-right font-mono tabular-nums">{row.samples.toLocaleString()}</td>
                <td className="py-2 text-right font-mono tabular-nums">{row.first.toFixed(3)}</td>
                <td className="py-2 text-right font-mono tabular-nums">{row.latest.toFixed(3)}</td>
              </tr>
              {expanded && <TaskRows points={points} name={row.name} />}
            </Fragment>
          )
        })}
      </tbody>
    </table>
  )
}

export function ValidationCurve({
  points,
  totalSteps,
}: {
  points: TrainingValidationPoint[]
  totalSteps: number
}): React.JSX.Element {
  const datasets = validationDatasets(points)
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <span className="h-[2px] w-4 bg-foreground" />
          All datasets
        </span>
        {datasets.map((name, index) => (
          <span
            key={name}
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            <DashMark index={index} />
            {name}
          </span>
        ))}
      </div>
      <div className="h-[280px] w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart data={validationRows(points)} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid stroke="hsl(var(--border) / 0.6)" />
            <XAxis
              dataKey="step"
              type="number"
              domain={[0, totalSteps]}
              ticks={stepTicks(totalSteps)}
              tickLine={false}
              axisLine={false}
              tick={AXIS_TICK}
              tickFormatter={(value: number) => value.toLocaleString()}
            />
            <YAxis
              domain={rewardDomain(points)}
              tickCount={5}
              tickLine={false}
              axisLine={false}
              width={44}
              tick={AXIS_TICK}
              tickFormatter={(value: number) => value.toFixed(2)}
            />
            <Tooltip
              content={({ active, payload }) => <ValidationTooltip active={active} payload={payload} />}
              cursor={{ stroke: 'hsl(var(--muted-foreground) / 0.4)', strokeDasharray: '3 3' }}
            />
            {datasets.map((name, index) => (
              <Line
                key={name}
                name={name}
                dataKey={(row: ValidationRow) => row.datasets[name] ?? null}
                stroke={tint(index)}
                strokeWidth={1.5}
                strokeDasharray={dash(index)}
                dot={{ r: 3, fill: tint(index), stroke: 'none' }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
            <Line
              dataKey="reward"
              stroke="hsl(var(--foreground))"
              strokeWidth={1.5}
              dot={{ r: 2.5, fill: 'hsl(var(--foreground))', stroke: 'none' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <Summary points={points} />
    </div>
  )
}
